import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ConnectorTypeEnum } from '@shared/enums/connector-type';
import { LoadBalancingStrategyEnum } from '../../../shared/enums/load-balancing-strategy.enum';
import type { StationData } from '../StationDetails/StationDetails';

export interface StationFormData {
  idTag: string;
  connectorTypes: ConnectorTypeEnum[];
  price: number;
  option: number;
  address: string;
  latitude: number;
  longitude: number;
  aroundTheClock: boolean;
  startWorkingTime: string;
  endWorkingTime: string;
  currentLimit: number | undefined;
  loadBalancingStrategy: LoadBalancingStrategyEnum;
  downtimePricePerMinute: number;
  downtimeFreeMinutes: number;
  organizationId: string | undefined;
}

export const INITIAL_FORM_DATA: StationFormData = {
  idTag: '',
  connectorTypes: [],
  price: 12,
  option: 22,
  address: '',
  latitude: 50.4501,
  longitude: 30.5234,
  aroundTheClock: false,
  startWorkingTime: '08:00',
  endWorkingTime: '22:00',
  currentLimit: undefined,
  loadBalancingStrategy: LoadBalancingStrategyEnum.None,
  downtimePricePerMinute: 0.25,
  downtimeFreeMinutes: 60,
  organizationId: undefined,
};

export const STEP_DEFS = [
  { id: 'general', labelKey: 'dashboard.createStationV2.stepGeneral', icon: 'info' },
  { id: 'location', labelKey: 'dashboard.createStationV2.stepLocation', icon: 'location_on' },
  { id: 'schedule', labelKey: 'dashboard.createStationV2.stepSchedule', icon: 'schedule' },
] as const;

export const STEPS = STEP_DEFS;

export type LocalizedStep = { id: string; label: string; icon: string };

export const useLocalizedSteps = (): LocalizedStep[] => {
  const { t } = useTranslation();
  return useMemo(
    () => STEP_DEFS.map((s) => ({ id: s.id, label: t(s.labelKey), icon: s.icon })),
    [t],
  );
};

/**
 * Station (as loaded on the details page) → wizard form state.
 * Single source for the 14-field mapping used both for the edit tab's
 * initial state and its cancel/reset.
 */
export function stationToFormData(station: StationData): StationFormData {
  return {
    idTag: station.idTag, // Keep for type compatibility, won't be shown in edit mode
    connectorTypes: station.connectorTypes,
    option: station.option,
    price: station.price,
    address: station.address,
    latitude: station.latitude,
    longitude: station.longitude,
    aroundTheClock: station.aroundTheClock,
    startWorkingTime: station.startWorkingTime || '00:00',
    endWorkingTime: station.endWorkingTime || '23:59',
    currentLimit: station.currentLimit,
    loadBalancingStrategy: station.loadBalancingStrategy ?? LoadBalancingStrategyEnum.None,
    downtimePricePerMinute: station.downtimePricePerMinute ?? 0.25,
    downtimeFreeMinutes: station.downtimeFreeMinutes ?? 60,
    organizationId: station.organizationId,
  };
}

/**
 * Wizard form state → API payload shared by create and update.
 * (Create additionally sends `idTag`; update never does.)
 *
 * `isAdmin` gates `organizationId`: non-admins must NEVER send this field.
 * `POST /stations` ignores it from non-admins server-side, but
 * `PATCH /stations/:id` rejects it from non-admins with 403 — and
 * `stationToFormData` will have copied an existing `organizationId` onto
 * the edit form even when the viewer isn't an admin (e.g. a non-admin
 * viewing/editing an org-owned station), so the field can't rely on being
 * merely unset. Gating here, at the single call site both create and edit
 * share, is the one place that is safe regardless of how formData was
 * populated.
 *
 * `mode` distinguishes the two meanings of "no organisation":
 *   - edit:   null, an explicit instruction to clear the field. undefined
 *             would be dropped by JSON.stringify and silently do nothing.
 *   - create: undefined, which lets the server attach the creator's own
 *             organisation (resolveStationOrganizationId). Sending null
 *             here would change create semantics.
 */
export function formDataToPayload(
  formData: StationFormData,
  isAdmin: boolean,
  mode: 'create' | 'edit',
) {
  const organizationId = !isAdmin
    ? undefined
    : formData.organizationId || (mode === 'edit' ? null : undefined);

  return {
    connectorTypes: formData.connectorTypes,
    option: formData.option,
    price: formData.price,
    address: formData.address,
    latitude: formData.latitude,
    longitude: formData.longitude,
    aroundTheClock: formData.aroundTheClock,
    startWorkingTime: formData.aroundTheClock ? undefined : formData.startWorkingTime,
    endWorkingTime: formData.aroundTheClock ? undefined : formData.endWorkingTime,
    currentLimit: formData.currentLimit,
    loadBalancingStrategy: formData.loadBalancingStrategy,
    downtimePricePerMinute: formData.downtimePricePerMinute,
    downtimeFreeMinutes: formData.downtimeFreeMinutes,
    organizationId: organizationId as string | null | undefined,
  };
}
